import { z } from 'zod/v3';
import { DatasetModel } from '../model/dataset.model';
import { err, ok } from 'neverthrow';
import { Assert } from '@cotera/utilities';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import { DATASET_ACTION_GROUP } from './constants';
import { SaveDatasetAction } from './save-dataset.action';

type ColumnType = 'llm' | 'expression' | 'taxonomy' | 'tool-call';

type Params = {
  type?: ColumnType | undefined;
  strategy?: 'push' | 'replace';
};

export class NewColumnAction extends BaseAction<Params> {
  title = 'New Column';
  description = 'Create a new column';
  inputSchema = z.object({
    type: z.enum(['llm', 'expression', 'taxonomy', 'tool-call']).optional(),
    strategy: z.enum(['push', 'replace']).optional(),
  });
  group = DATASET_ACTION_GROUP;
  icon = 'plus';

  constructor(
    private navigator: {
      push: (props: { title: string; route: string }) => void;
      pushAndReplace: (props: { title: string; route: string }) => void;
    }
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(DatasetModel);
  }

  async execute(
    { type, strategy = 'push' }: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const dataset = context.targetedModelOfType(DatasetModel);

    Assert.assert(dataset !== null, 'Dataset model not found');

    if (dataset.isNew) {
      const result = await context
        .getAction(SaveDatasetAction)!
        .askOrExecute({}, context);
      if (result.isErr()) {
        return err(result.error);
      }
    }

    const datasetName = dataset.currentNameInGraph.snapshot();
    Assert.assert(datasetName !== null); // must exist after saving

    const { title, route } = getNavProps(type, datasetName);

    if (strategy === 'replace') {
      this.navigator.pushAndReplace({
        title,
        route,
      });
    } else {
      this.navigator.push({
        title,
        route,
      });
    }

    return ok({});
  }
}

function getNavProps(
  maybeColType: ColumnType | undefined,
  datasetName: string
): { title: string; route: string } {
  const baseRoute = `/dataset/${encodeURIComponent(datasetName)}/column/new`;
  if (maybeColType === undefined) {
    return { title: `${datasetName}/New Column`, route: baseRoute };
  }

  const subTitle: Record<ColumnType, string> = {
    llm: 'New LLM Column',
    expression: 'New Formula Column',
    taxonomy: 'New Taxonony Column',
    'tool-call': 'New Tool Call Column',
  };

  const routeSuffix: Record<ColumnType, string> = {
    llm: 'llm',
    expression: 'expression',
    taxonomy: 'taxonomy',
    'tool-call': 'tool-call',
  };

  return {
    title: `${datasetName}/${subTitle[maybeColType]}`,
    route: `${baseRoute}/${routeSuffix[maybeColType]}`,
  };
}
